import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircleIcon } from "@heroicons/react/24/outline";

/**
 * FeatureShowcaseAnimation — auto-playing feature reel for the home page.
 *
 * Renders a 16:9 "video-like" panel (floored at 30rem tall, so on phones it
 * becomes portrait) that cycles through 5 scenes
 * (Chat, Voice, Knowledge Base, Pipeline, Triggers). Each scene has a brand
 * heading + an illustrated animation. Users can also click the dots at the
 * bottom to jump scenes.
 *
 * No actual video file — pure CSS + framer-motion, GPU-accelerated, sharp at
 * any resolution. Drop into the same styled container the Shopify page uses
 * for its <video> and it reads as the hero motion piece.
 *
 * Centre animated elements with flex, never Tailwind `translate-*`: framer
 * writes an inline `transform` on anything animating x/y/scale, which
 * silently discards the class's translate.
 *
 * Audio narration:
 *   Optional MP3 narration generated from Telnyx TTS (same voices the live
 *   call agent uses) lives at `/audio/feature-showcase/scene-<id>.mp3`. Files
 *   are pre-rendered by `backend/src/scripts/generateFeatureShowcaseAudio.ts`
 *   and committed to `frontend/public/`. The component starts MUTED (browsers
 *   block autoplay with sound) and exposes a sound toggle in the top-right.
 *   When unmuted, scenes auto-advance on `audio.ended` instead of the timer,
 *   so the visual sync follows the narration length.
 */

const SCENE_DURATION_MS = 4500;
const AUDIO_BASE_URL = "/audio/feature-showcase";

type Scene = {
    id: string;
    label: string;
    title: string;
    subtitle: string;
    accent: string; // tailwind from-X to-Y for gradient text
    glow: string; // tailwind bg-X/30 for backdrop glow
    Render: () => JSX.Element;
};

const fadeIn = (delay = 0) => ({
    initial: { opacity: 0, y: 12 },
    animate: { opacity: 1, y: 0, transition: { delay, duration: 0.5, ease: "easeOut" } },
    exit: { opacity: 0, y: -8, transition: { duration: 0.25 } },
});

const popIn = (delay = 0) => ({
    initial: { opacity: 0, scale: 0.9 },
    animate: { opacity: 1, scale: 1, transition: { delay, duration: 0.5, ease: [0.16, 1, 0.3, 1] } },
    exit: { opacity: 0, scale: 0.95, transition: { duration: 0.25 } },
});

/* ───────────────────── Scene 1 · Chat Widget ───────────────────── */
const ChatScene = () => (
    <div className="flex h-full w-full items-center justify-center">
        {/* Height-led so the mock fits whatever the title leaves; capped to the width on phones. */}
        <div className="relative aspect-[16/10] h-full max-w-full">
            {/* Browser window mock */}
            <motion.div
                {...popIn(0.1)}
                className="h-full w-full rounded-2xl border border-white/15 bg-slate-900/80 shadow-2xl overflow-hidden"
            >
                <div className="flex items-center gap-1.5 px-3 py-2 border-b border-white/10 bg-slate-900/90">
                    <span className="h-2 w-2 rounded-full bg-red-400/70" />
                    <span className="h-2 w-2 rounded-full bg-yellow-400/70" />
                    <span className="h-2 w-2 rounded-full bg-emerald-400/70" />
                    <span className="ml-3 text-[10px] text-slate-500">acme.com</span>
                </div>
                <div className="grid grid-cols-3 gap-2 p-4 opacity-50">
                    <div className="h-2 rounded bg-white/10" />
                    <div className="h-2 rounded bg-white/10 col-span-2" />
                    <div className="h-12 rounded bg-white/5 col-span-3" />
                    <div className="h-12 rounded bg-white/5" />
                    <div className="h-12 rounded bg-white/5 col-span-2" />
                </div>
            </motion.div>

            {/* Chat widget popping up */}
            <motion.div
                initial={{ opacity: 0, y: 30, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ delay: 0.6, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="absolute -bottom-2 -right-2 w-[68%] sm:-bottom-4 sm:-right-8 sm:w-[56%] rounded-2xl border border-white/15 bg-slate-900/95 shadow-2xl backdrop-blur-md overflow-hidden"
            >
                <div className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-orange-500 to-pink-500 text-white text-xs font-semibold">
                    <div className="h-5 w-5 rounded-full bg-white/20 flex items-center justify-center text-[10px]">F</div>
                    FloGPT
                </div>
                <div className="p-3 space-y-2">
                    <motion.div
                        initial={{ opacity: 0, x: -8 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 1.0, duration: 0.3 }}
                        className="text-[11px] bg-white/10 text-white rounded-lg px-2.5 py-1.5 max-w-[85%]"
                    >
                        Hi! How can I help today?
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, x: 8 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 1.6, duration: 0.3 }}
                        className="text-[11px] bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-lg px-2.5 py-1.5 max-w-[85%] ml-auto"
                    >
                        Where's my order?
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, x: -8 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 2.2, duration: 0.3 }}
                        className="text-[11px] bg-white/10 text-white rounded-lg px-2.5 py-1.5 max-w-[85%]"
                    >
                        Order #1024 ships tomorrow ✨
                    </motion.div>
                </div>
            </motion.div>
        </div>
    </div>
);

/* ───────────────────── Scene 2 · Voice Agent ───────────────────── */
const VoiceScene = () => (
    <div className="relative h-full w-full flex items-center justify-center">
        <motion.div
            {...popIn(0.1)}
            className="relative w-full sm:w-[80%] max-w-[620px] rounded-2xl border border-white/15 bg-slate-900/80 shadow-2xl p-4 lg:p-5 backdrop-blur-md"
        >
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <motion.div
                        className="h-10 w-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white font-semibold"
                        animate={{ boxShadow: ["0 0 0 0 rgba(16,185,129,0.5)", "0 0 0 12px rgba(16,185,129,0)", "0 0 0 0 rgba(16,185,129,0)"] }}
                        transition={{ duration: 1.8, repeat: Infinity }}
                    >
                        A
                    </motion.div>
                    <div>
                        <div className="text-white text-sm font-semibold">Aria · AI Voice Agent</div>
                        <div className="text-[10px] text-slate-400">Acme Dental · +1 (555) 010-2025</div>
                    </div>
                </div>
                <div className="flex items-center gap-1.5 text-[10px] text-emerald-300">
                    <motion.span
                        className="h-1.5 w-1.5 rounded-full bg-emerald-400"
                        animate={{ opacity: [0.4, 1, 0.4] }}
                        transition={{ duration: 1.4, repeat: Infinity }}
                    />
                    Live · 00:42
                </div>
            </div>

            {/* Waveform */}
            <div className="mt-3 lg:mt-4 flex items-center justify-center gap-0.5 sm:gap-1 h-8 lg:h-10">
                {Array.from({ length: 28 }).map((_, i) => (
                    <motion.span
                        key={i}
                        className="w-1 rounded-full bg-gradient-to-t from-emerald-400 to-teal-300"
                        animate={{ height: [4, 6 + (i % 5) * 5, 4] }}
                        transition={{ duration: 0.9 + (i % 4) * 0.15, repeat: Infinity, delay: i * 0.04 }}
                    />
                ))}
            </div>

            {/* Transcript bubbles */}
            <div className="mt-3 lg:mt-4 space-y-2">
                <motion.div
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6, duration: 0.3 }}
                    className="text-[11px] bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-lg px-3 py-1.5 w-fit max-w-[92%] sm:max-w-[85%]"
                >
                    <span className="opacity-70 mr-1.5 text-[9px] uppercase">AI</span>
                    Thanks for calling — how can I help?
                </motion.div>
                <motion.div
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.4, duration: 0.3 }}
                    className="text-[11px] bg-slate-800/80 text-white rounded-lg px-3 py-1.5 w-fit max-w-[92%] sm:max-w-[85%] ml-auto"
                >
                    <span className="opacity-70 mr-1.5 text-[9px] uppercase">Caller</span>
                    Book me a cleaning Friday at 4pm.
                </motion.div>
                <motion.div
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 2.2, duration: 0.3 }}
                    className="text-[11px] bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-lg px-3 py-1.5 w-fit max-w-[92%] sm:max-w-[85%]"
                >
                    <span className="opacity-70 mr-1.5 text-[9px] uppercase">AI</span>
                    Booked. Confirmation SMS on its way.
                </motion.div>
            </div>
        </motion.div>
    </div>
);

/* ───────────────────── Scene 3 · Knowledge Base ───────────────────── */
const KnowledgeScene = () => {
    // The offsets are px tuned for a desktop-width stage; at phone width they
    // land the docs on (or past) the panel edge. Read once per mount, which is
    // every time the scene comes round.
    const [spread] = useState(() =>
        typeof window.matchMedia === "function" && window.matchMedia("(max-width: 639px)").matches ? 0.55 : 1,
    );
    const docs = [
        { label: "PDF", color: "from-rose-500 to-pink-500", x: -180, y: -60 },
        { label: "DOCS", color: "from-blue-500 to-cyan-500", x: -200, y: 70 },
        { label: "FAQ", color: "from-amber-500 to-orange-500", x: 180, y: -70 },
        { label: "WEB", color: "from-purple-500 to-indigo-500", x: 200, y: 60 },
    ];
    return (
        <div className="h-full w-full flex flex-col items-center">
            <div className="relative min-h-0 w-full flex-1 flex items-center justify-center">
                {/* Central AI orb */}
                <motion.div
                    {...popIn(0.2)}
                    className="relative h-20 w-20 sm:h-28 sm:w-28 rounded-full bg-gradient-to-br from-orange-500 via-pink-500 to-purple-500 shadow-[0_0_60px_rgba(236,72,153,0.4)] flex items-center justify-center"
                >
                    <motion.div
                        className="absolute inset-0 rounded-full border-2 border-white/30"
                        animate={{ scale: [1, 1.4, 1.6], opacity: [0.6, 0.2, 0] }}
                        transition={{ duration: 2.2, repeat: Infinity }}
                    />
                    <motion.div
                        className="absolute inset-0 rounded-full border-2 border-white/30"
                        animate={{ scale: [1, 1.4, 1.6], opacity: [0.6, 0.2, 0] }}
                        transition={{ duration: 2.2, repeat: Infinity, delay: 0.7 }}
                    />
                    <span className="text-white text-xl sm:text-2xl font-bold">AI</span>
                </motion.div>

                {/* Docs flying in */}
                {docs.map((d, i) => {
                    const x = d.x * spread;
                    return (
                        <motion.div
                            key={d.label}
                            initial={{ opacity: 0, x: x * 1.6, y: d.y * 1.6, scale: 0.6 }}
                            animate={{
                                opacity: [0, 1, 1, 0.4],
                                x: [x * 1.6, x, x * 0.4, 0],
                                y: [d.y * 1.6, d.y, d.y * 0.4, 0],
                                scale: [0.6, 1, 1, 0.5],
                            }}
                            transition={{ delay: 0.4 + i * 0.15, duration: 2.5, ease: "easeInOut" }}
                            className={`absolute h-12 w-16 rounded-lg bg-gradient-to-br ${d.color} flex items-center justify-center text-[10px] font-bold text-white shadow-lg`}
                        >
                            {d.label}
                        </motion.div>
                    );
                })}
            </div>

            {/* Citation bubble */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 2.6, duration: 0.5 }}
                className="mb-2 sm:mb-6 flex max-w-[90%] sm:max-w-[70%] items-center gap-2 rounded-lg bg-slate-900/80 border border-white/10 backdrop-blur px-4 py-2 text-xs text-slate-200 text-left sm:text-center"
            >
                <CheckCircleIcon className="h-4 w-4 shrink-0 text-emerald-400" aria-hidden="true" />
                Answers grounded with citations to your own content
            </motion.div>
        </div>
    );
};

/* ───────────────────── Scene 4 · Pipeline / Workflow ───────────────────── */
const PipelineScene = () => {
    const nodes = [
        { name: "Greet", color: "from-emerald-500 to-teal-500", row: 0, col: 0 },
        { name: "Listen", color: "from-indigo-500 to-purple-500", row: 0, col: 1 },
        { name: "KB Lookup", color: "from-orange-500 to-pink-500", row: 0, col: 2 },
        { name: "Tool Call", color: "from-blue-500 to-cyan-500", row: 1, col: 0 },
        { name: "Book / SMS", color: "from-yellow-500 to-orange-500", row: 1, col: 1 },
        { name: "Transfer / End", color: "from-slate-500 to-slate-700", row: 1, col: 2 },
    ];
    return (
        <div className="relative h-full w-full flex items-center justify-center">
            <div className="grid grid-cols-3 gap-x-3 gap-y-5 sm:gap-x-12 sm:gap-y-6 w-full sm:w-[70%] max-w-[640px] relative">
                {/* Connecting lines (svg overlay) */}
                <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
                    {[
                        { x1: 25, y1: 25, x2: 50, y2: 25, delay: 0.5 },
                        { x1: 50, y1: 25, x2: 75, y2: 25, delay: 0.8 },
                        { x1: 75, y1: 25, x2: 25, y2: 75, delay: 1.1 },
                        { x1: 25, y1: 75, x2: 50, y2: 75, delay: 1.4 },
                        { x1: 50, y1: 75, x2: 75, y2: 75, delay: 1.7 },
                    ].map((l, i) => (
                        <motion.line
                            key={i}
                            x1={l.x1}
                            y1={l.y1}
                            x2={l.x2}
                            y2={l.y2}
                            stroke="url(#pipelineGrad)"
                            strokeWidth="0.4"
                            strokeDasharray="1.5 1.5"
                            initial={{ pathLength: 0, opacity: 0 }}
                            animate={{ pathLength: 1, opacity: 0.7 }}
                            transition={{ delay: l.delay, duration: 0.45, ease: "easeOut" }}
                        />
                    ))}
                    <defs>
                        <linearGradient id="pipelineGrad">
                            <stop offset="0%" stopColor="#f97316" />
                            <stop offset="50%" stopColor="#ec4899" />
                            <stop offset="100%" stopColor="#3b82f6" />
                        </linearGradient>
                    </defs>
                </svg>

                {nodes.map((n, idx) => (
                    <motion.div
                        key={n.name}
                        initial={{ opacity: 0, y: 14, scale: 0.92 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ delay: 0.15 * idx, duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                        className={`relative flex items-center justify-center rounded-xl border border-white/10 bg-gradient-to-r ${n.color} p-2 sm:p-3 text-center text-[11px] sm:text-xs leading-tight font-semibold text-white shadow-xl`}
                    >
                        {n.name}
                    </motion.div>
                ))}
            </div>
        </div>
    );
};

/* ───────────────────── Scene 5 · Outbound Triggers ───────────────────── */
const TriggerScene = () => {
    const sources = [
        { label: "Webhook", color: "from-orange-500 to-pink-500" },
        { label: "Shopify", color: "from-emerald-500 to-teal-500" },
        { label: "Schedule", color: "from-indigo-500 to-purple-500" },
        { label: "Data Watch", color: "from-blue-500 to-cyan-500" },
    ];
    return (
        <div className="relative h-full w-full flex items-center justify-center">
            <div className="relative grid grid-cols-[auto_1fr_auto] items-center gap-4 sm:gap-10 w-full sm:w-[80%] max-w-[680px]">
                {/* Sources column */}
                <div className="flex flex-col gap-2">
                    {sources.map((s, i) => (
                        <motion.div
                            key={s.label}
                            initial={{ opacity: 0, x: -16 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.15 * i, duration: 0.35 }}
                            className={`rounded-lg bg-gradient-to-r ${s.color} px-3 py-1.5 text-[11px] font-semibold text-white shadow-lg w-24 sm:w-28 text-center`}
                        >
                            {s.label}
                        </motion.div>
                    ))}
                </div>

                {/* Animated event packets */}
                <div className="relative h-32">
                    {sources.map((_, i) => (
                        <motion.span
                            key={i}
                            className="absolute top-1/2 left-0 -translate-y-1/2 h-1.5 w-3 rounded-full bg-gradient-to-r from-orange-400 to-pink-400 shadow-[0_0_10px_rgba(249,115,22,0.7)]"
                            style={{ top: `${20 + i * 22}%` }}
                            animate={{ x: ["0%", "100%"], opacity: [0, 1, 1, 0] }}
                            transition={{ delay: 0.4 + i * 0.25, duration: 1.4, repeat: Infinity, repeatDelay: 1.4 }}
                        />
                    ))}
                </div>

                {/* Phone target */}
                <motion.div
                    {...popIn(0.6)}
                    className="relative h-16 w-16 sm:h-24 sm:w-24 rounded-2xl bg-gradient-to-br from-orange-500 to-pink-500 shadow-2xl flex items-center justify-center"
                >
                    <motion.div
                        className="absolute inset-0 rounded-2xl border-2 border-orange-300"
                        animate={{ scale: [1, 1.35, 1.5], opacity: [0.6, 0.1, 0] }}
                        transition={{ duration: 1.6, repeat: Infinity }}
                    />
                    <svg className="h-7 w-7 sm:h-10 sm:w-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M3 5a2 2 0 012-2h2.5l1.5 4-2 1a11 11 0 005.5 5.5l1-2 4 1.5V19a2 2 0 01-2 2A16 16 0 013 5z" />
                    </svg>
                </motion.div>
            </div>
        </div>
    );
};

/* ───────────────────── Scenes registry ───────────────────── */
const SCENES: Scene[] = [
    {
        id: "chat",
        label: "01 · Chat Widget",
        title: "Embed an AI chat widget in seconds",
        subtitle: "One script tag · brandable · multilingual",
        accent: "from-orange-400 via-pink-400 to-rose-400",
        glow: "bg-orange-500/20",
        Render: ChatScene,
    },
    {
        id: "voice",
        label: "02 · Voice Agent",
        title: "AI agents that answer & place phone calls",
        subtitle: "Inbound + outbound · Managed numbers · Twilio · Jio · MSG91",
        accent: "from-emerald-400 via-teal-400 to-cyan-400",
        glow: "bg-emerald-500/20",
        Render: VoiceScene,
    },
    {
        id: "knowledge",
        label: "03 · Knowledge Base",
        title: "Grounded in your own knowledge",
        subtitle: "Crawl docs, PDFs and websites · cited answers",
        accent: "from-pink-400 via-rose-400 to-orange-400",
        glow: "bg-pink-500/20",
        Render: KnowledgeScene,
    },
    {
        id: "pipeline",
        label: "04 · Pipeline & Workflow",
        title: "Design conversation flows visually",
        subtitle: "Drag nodes · branch on intent · call tools",
        accent: "from-indigo-400 via-purple-400 to-pink-400",
        glow: "bg-indigo-500/20",
        Render: PipelineScene,
    },
    {
        id: "triggers",
        label: "05 · Outbound Triggers",
        title: "Triggers fire calls automatically",
        subtitle: "Webhooks · Shopify events · schedules · data watches",
        accent: "from-amber-400 via-orange-400 to-pink-400",
        glow: "bg-amber-500/20",
        Render: TriggerScene,
    },
];

interface FeatureShowcaseAnimationProps {
    autoPlay?: boolean;
    /** Skip the audio path entirely — useful for tests / SSR snapshots. */
    disableAudio?: boolean;
}

export default function FeatureShowcaseAnimation({ autoPlay = true, disableAudio = false }: FeatureShowcaseAnimationProps) {
    const [scene, setScene] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    // Default muted: browsers block autoplay with sound until a user gesture.
    // The toggle in the top-right unmutes and triggers playback of the
    // current scene's narration.
    const [muted, setMuted] = useState(true);
    // Captured from `<audio>` `onLoadedMetadata`; drives the active progress
    // bar duration when sound is on so the bar finishes exactly when the
    // narration ends and we advance to the next scene.
    const [audioDurationMs, setAudioDurationMs] = useState<number | null>(null);
    const audioRef = useRef<HTMLAudioElement | null>(null);

    const active = SCENES[scene];
    const audioOn = !muted && !disableAudio;
    const sceneDurationMs = audioOn && audioDurationMs ? audioDurationMs : SCENE_DURATION_MS;

    // Visual auto-advance — only used when audio is OFF. When audio is ON,
    // we advance from the `<audio>` `onEnded` handler so video stays
    // perfectly synced with narration length (which varies per scene).
    useEffect(() => {
        if (!autoPlay || isPaused) return;
        if (audioOn) return;
        const t = window.setTimeout(
            () => setScene((s) => (s + 1) % SCENES.length),
            SCENE_DURATION_MS,
        );
        return () => window.clearTimeout(t);
    }, [scene, autoPlay, isPaused, audioOn]);

    // Audio playback orchestration. Reset duration so the progress bar
    // doesn't briefly use the previous scene's length while the next file
    // loads metadata.
    useEffect(() => {
        const a = audioRef.current;
        if (!a) return;
        setAudioDurationMs(null);
        if (!audioOn || isPaused) {
            try { a.pause(); } catch { /* ignore */ }
            return;
        }
        a.src = `${AUDIO_BASE_URL}/scene-${active.id}.mp3`;
        a.currentTime = 0;
        const playPromise = a.play();
        if (playPromise && typeof playPromise.catch === "function") {
            playPromise.catch(() => {
                // Autoplay was blocked or the file 404'd — silently revert
                // to the visual-only path so the reel keeps cycling.
                setMuted(true);
            });
        }
    }, [scene, audioOn, isPaused, active.id]);

    const handleLoadedMetadata = () => {
        const dur = audioRef.current?.duration;
        if (dur && Number.isFinite(dur) && dur > 0) {
            setAudioDurationMs(Math.round(dur * 1000));
        }
    };

    const handleAudioEnded = () => {
        if (!autoPlay || isPaused) return;
        setScene((s) => (s + 1) % SCENES.length);
    };

    return (
        <div
            className="relative flex flex-col w-full aspect-video min-h-[30rem] overflow-hidden bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
        >
            {/* Hidden narration audio — driven by the scene-change effect above. */}
            {!disableAudio && (
                <audio
                    ref={audioRef}
                    onLoadedMetadata={handleLoadedMetadata}
                    onEnded={handleAudioEnded}
                    onError={() => setMuted(true)}
                    preload="metadata"
                    aria-hidden="true"
                />
            )}
            {/* Background grid */}
            <div
                className="absolute inset-0 opacity-30"
                style={{
                    backgroundImage:
                        "linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)",
                    backgroundSize: "32px 32px",
                }}
            />

            {/* Scene-tinted glow */}
            <AnimatePresence mode="wait">
                <motion.div
                    key={`glow-${active.id}`}
                    className={`pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 h-[420px] w-[420px] rounded-full blur-3xl ${active.glow}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.6 }}
                />
            </AnimatePresence>

            <div className="relative z-10 flex items-center justify-between gap-3 px-4 pt-4 sm:px-5 sm:pt-5">
                {/* Scene label (top-left) */}
                <div className="flex min-w-0 items-center gap-2">
                    <div className="h-6 w-6 shrink-0 rounded-md bg-white/10 border border-white/15 flex items-center justify-center overflow-hidden">
                        <img
                            src="/images/flogpt-iso.png"
                            alt="FloGPT"
                            width={48}
                            height={48}
                            loading="lazy"
                            decoding="async"
                            className="h-full w-full object-contain"
                        />
                    </div>
                    <span className="truncate text-[11px] font-medium tracking-wider text-slate-400 uppercase">
                        FloGPT in motion
                    </span>
                </div>

                {/* Live pill + sound toggle (top-right) */}
                <div className="flex shrink-0 items-center gap-2">
                    {!disableAudio && (
                        <button
                            type="button"
                            onClick={() => setMuted((m) => !m)}
                            aria-label={audioOn ? "Mute narration" : "Unmute narration"}
                            title={audioOn ? "Mute narration" : "Play with sound"}
                            className="group flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 backdrop-blur px-2.5 py-1 text-[10px] font-semibold text-white/80 hover:text-white hover:bg-white/10 transition"
                        >
                            {audioOn ? (
                                <svg className="h-3 w-3" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                    <path d="M3 10v4a1 1 0 001 1h3l4 3.5a1 1 0 001.7-.7V6.2a1 1 0 00-1.7-.7L7 9H4a1 1 0 00-1 1z" />
                                    <path d="M16 8.5a4.5 4.5 0 010 7" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" />
                                    <path d="M18.5 6a8 8 0 010 12" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" />
                                </svg>
                            ) : (
                                <svg className="h-3 w-3" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                    <path d="M3 10v4a1 1 0 001 1h3l4 3.5a1 1 0 001.7-.7V6.2a1 1 0 00-1.7-.7L7 9H4a1 1 0 00-1 1z" />
                                    <path d="M16 9l5 6m0-6l-5 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" fill="none" />
                                </svg>
                            )}
                            <span>{audioOn ? "Sound on" : "Play sound"}</span>
                        </button>
                    )}
                    <div className="flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 backdrop-blur px-2.5 py-1 text-[10px] text-emerald-300 font-semibold">
                        <motion.span
                            className="h-1.5 w-1.5 rounded-full bg-emerald-400"
                            animate={{ opacity: [0.4, 1, 0.4] }}
                            transition={{ duration: 1.4, repeat: Infinity }}
                        />
                        Demo
                    </div>
                </div>
            </div>

            {/* Scene title block */}
            <div className="relative z-10 shrink-0 px-5 pt-4 sm:px-8 sm:pt-5 text-center">
                <AnimatePresence mode="wait">
                    <motion.div key={`title-${active.id}`} {...fadeIn(0)}>
                        <div className="text-[10px] tracking-[0.2em] uppercase text-slate-400 mb-2">{active.label}</div>
                        <h3 className={`text-xl sm:text-2xl md:text-3xl font-bold leading-tight bg-gradient-to-r ${active.accent} bg-clip-text text-transparent`}>
                            {active.title}
                        </h3>
                        <p className="mt-1.5 text-xs sm:text-sm text-slate-400">{active.subtitle}</p>
                    </motion.div>
                </AnimatePresence>
            </div>

            {/* Scene illustration area — flex-1, so a title that wraps pushes it down instead of running under it. */}
            <div className="relative min-h-0 flex-1 mx-4 mt-3 mb-10 sm:mx-6 sm:mt-6 sm:mb-12">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={`scene-${active.id}`}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0, transition: { duration: 0.45, ease: "easeOut" } }}
                        exit={{ opacity: 0, y: -8, transition: { duration: 0.25 } }}
                        className="absolute inset-0"
                    >
                        <active.Render />
                    </motion.div>
                </AnimatePresence>
            </div>

            {/* Progress dots */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1.5">
                {SCENES.map((s, i) => (
                    <button
                        key={s.id}
                        type="button"
                        onClick={() => setScene(i)}
                        aria-label={`Jump to ${s.label}`}
                        className="group relative h-1.5 rounded-full overflow-hidden bg-white/15 hover:bg-white/30 transition-colors"
                        style={{ width: i === scene ? 28 : 6 }}
                    >
                        {i === scene && !isPaused && (
                            <motion.span
                                key={`bar-${s.id}-${scene}-${audioOn ? "audio" : "timer"}-${audioDurationMs ?? "default"}`}
                                className="absolute inset-y-0 left-0 bg-gradient-to-r from-orange-400 to-pink-400"
                                initial={{ width: "0%" }}
                                animate={{ width: "100%" }}
                                transition={{ duration: sceneDurationMs / 1000, ease: "linear" }}
                            />
                        )}
                        {i === scene && isPaused && (
                            <span className="absolute inset-0 bg-gradient-to-r from-orange-400 to-pink-400" />
                        )}
                    </button>
                ))}
            </div>
        </div>
    );
}
